import { useState } from 'react'
import clsx from 'clsx'
import { Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useCanvasTheme } from '@/features/studio/canvas/theme/CanvasThemeContext'
import { EDGE_ANIMATIONS, NODE_THEMES, type EdgeAnimId, type NodeThemeId } from '@/features/studio/canvas/theme/registry'
import { ProtoTabs } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'

type PickerTab = 'nodes' | 'edges'
function NodeThemePreview({ themeId, accent, active }: { themeId: NodeThemeId; accent: string; active: boolean }) {
  return (
    <div
      data-node-theme={themeId}
      className={clsx('cn-preview-wrap', active && 'cn-preview-wrap--active')}
    >
      <div className="cn-node cn-preview-node" style={{ '--cn-category': accent } as React.CSSProperties}>
        <header className="cn-head">
          <div className="cn-head__main">
            <span className="cn-head__stripe" style={{ background: accent }} />
            <div className="cn-head__text">
              <span className="cn-head__cat" style={{ color: accent }}>
                GEN
              </span>
              <span className="cn-head__title">Preview</span>
            </div>
          </div>
        </header>
        <div className="cn-body">
          <div className="cn-hero cn-hero--169">
            <span className="cn-hero__empty">—</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function EdgeAnimPreview({ animId, accent, active }: { animId: EdgeAnimId; accent: string; active: boolean }) {
  return (
    <svg viewBox="0 0 120 24" className={clsx('cn-edge-preview', active && 'cn-edge-preview--active')} aria-hidden>
      <path
        d="M 4 12 C 40 4, 80 20, 116 12"
        fill="none"
        stroke={accent}
        strokeWidth={2.5}
        className={clsx('cn-edge-preview__path', `cn-edge-preview__path--${animId}`)}
      />
      {(animId === 'double-track' || animId === 'braid' || animId === 'rail' || animId === 'lane' || animId === 'flow-neon') && (
        <path
          d="M 4 10 C 40 2, 80 18, 116 10"
          fill="none"
          stroke={accent}
          strokeWidth={animId === 'flow-neon' ? 2 : 1}
          opacity={animId === 'flow-neon' ? 0.85 : 0.45}
          className={clsx(
            'cn-edge-preview__path',
            animId === 'flow-neon' ? 'cn-edge-preview__path--flow-neon-b' : `cn-edge-preview__path--${animId}-b`,
          )}
        />
      )}
    </svg>
  )
}

export function CanvasStylePicker() {
  const { t } = useTranslation()
  const { pickerOpen, closePicker, nodeTheme, edgeAnim, setNodeTheme, setEdgeAnim } = useCanvasTheme()
  const [tab, setTab] = useState<PickerTab>('nodes')

  if (!pickerOpen) return null

  const tabs = [
    { id: 'nodes' as const, label: `${t('canvas.theme.nodeSection')} ${NODE_THEMES.length}` },
    { id: 'edges' as const, label: `${t('canvas.theme.edgeSection')} ${EDGE_ANIMATIONS.length}` },
  ]

  return (
    <div className="cn-picker-backdrop" onClick={closePicker} role="presentation">
      <aside
        className="cn-picker"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="cn-picker-title"
      >
        <header className="cn-picker__head">
          <h2 id="cn-picker-title" className="cn-picker__title">
            {t('canvas.theme.pickerTitle')}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={closePicker}
            className="cn-picker__close"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="cn-picker__tabs">
          <ProtoTabs tabs={tabs} active={tab} onChange={setTab} />
        </div>

        <div className="cn-picker__body studio-scrollbar-hidden">
          {tab === 'nodes' ? (
            <div className="cn-picker__grid">
              {NODE_THEMES.map((theme) => {
                const id = theme.id as NodeThemeId
                const active = nodeTheme === id
                return (
                  <Button
                    key={id}
                    type="button"
                    variant="ghost"
                    onClick={() => setNodeTheme(id)}
                    className={clsx('cn-picker__card h-auto flex-col items-stretch', active && 'cn-picker__card--active')}
                  >
                    <NodeThemePreview themeId={id} accent={theme.accent} active={active} />
                    <span className="cn-picker__card-name">{t(theme.nameKey)}</span>
                    <span className="cn-picker__card-desc">{t(theme.descKey)}</span>
                    {active ? (
                      <span className="cn-picker__badge">
                        <Check className="h-3 w-3" />
                        {t('canvas.theme.selected')}
                      </span>
                    ) : null}
                  </Button>
                )
              })}
            </div>
          ) : (
            <div className="cn-picker__list">
              {EDGE_ANIMATIONS.map((anim) => {
                const id = anim.id as EdgeAnimId
                const active = edgeAnim === id
                return (
                  <Button
                    key={id}
                    type="button"
                    variant="ghost"
                    onClick={() => setEdgeAnim(id)}
                    className={clsx('cn-picker__row h-auto justify-start', active && 'cn-picker__row--active')}
                  >
                    <EdgeAnimPreview animId={id} accent={anim.accent} active={active} />
                    <span className="cn-picker__row-text">
                      <span className="cn-picker__row-name">{t(anim.nameKey)}</span>
                      <span className="cn-picker__row-desc">{t(anim.descKey)}</span>
                    </span>
                    {active ? <Check className="h-4 w-4 shrink-0 text-emerald-400" /> : null}
                  </Button>
                )
              })}
            </div>
          )}
        </div>
      </aside>
    </div>
  )
}
